import { useEffect, useState } from 'react';

const WEEK_STARTS_SUNDAY_KEY = 'weekStartsSunday';

/**
 * Custom hook for managing "week starts on Sunday" preference
 * Stores the preference in localStorage and applies it across all pages
 * 
 * @returns [weekStartsSunday, setWeekStartsSunday] - Tuple with current preference and setter function
 * 
 * @example
 * const [weekStartsSunday, setWeekStartsSunday] = useWeekStartsSunday();
 */
export function useWeekStartsSunday(): [boolean, (value: boolean) => void] {
  // Initialize from localStorage, default to false (Monday)
  const getInitialValue = (): boolean => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(WEEK_STARTS_SUNDAY_KEY);
      if (stored !== null) {
        return stored === 'true';
      }
    }
    return false;
  };
  
  const [weekStartsSunday, setWeekStartsSundayState] = useState<boolean>(getInitialValue);

  // Update localStorage when preference changes
  const setWeekStartsSunday = (value: boolean) => {
    setWeekStartsSundayState(value);
    if (typeof window !== 'undefined') {
      localStorage.setItem(WEEK_STARTS_SUNDAY_KEY, String(value));
    }
  };

  // Listen to storage changes (in case user opens multiple tabs)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === WEEK_STARTS_SUNDAY_KEY && e.newValue !== null) {
        setWeekStartsSundayState(e.newValue === 'true');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return [weekStartsSunday, setWeekStartsSunday];
}

